import { memo, useEffect, useRef } from "react";
import ReactHlsPlayer from "react-hls-player";
import type { components } from "../../../apis/gen";
import { BlockButton } from "../../../inJoy-components/Button/BlockButton";
import { IconButton } from "../../../inJoy-components/Button/IconButton";
import { Chips } from "../../../inJoy-components/Chips/Chips";
import { Icon } from "../../../inJoy-components/Icon/Icon";
import { isPC } from "../../../utils";
import { formatHeroDateTime } from "../../../utils/date-utils";
import { EVENT_STATES } from "../useEventState";

type Event = components["schemas"]["Event"];

// Memoized video player component to prevent reloading on parent re-renders
interface MemoizedHlsPlayerProps {
	src: string;
	poster: string;
	sessionIdx: number;
}

const MemoizedHlsPlayer = memo(
	({ src, poster, sessionIdx }: MemoizedHlsPlayerProps) => {
		const playerRef = useRef<HTMLVideoElement>(
			null,
		) as React.RefObject<HTMLVideoElement>;

		useEffect(() => {
			// Manually trigger play when component mounts
			if (playerRef.current) {
				playerRef.current.play().catch(() => {
					// Autoplay was prevented, user needs to interact
				});
			}
		}, []);

		return (
			<ReactHlsPlayer
				key={`hls-player-${sessionIdx}`}
				playerRef={playerRef}
				src={src}
				autoPlay={true}
				muted={true}
				playsInline={true}
				width="100%"
				height="100%"
				poster={poster}
				hlsConfig={{
					maxLoadingDelay: 4,
					minAutoBitrate: 0,
					lowLatencyMode: true,
				}}
				style={{ objectFit: "cover", width: "100%", height: "100%" }}
			/>
		);
	},
	(prevProps, nextProps) => {
		// Only re-render if src or sessionIdx changes
		return (
			prevProps.src === nextProps.src &&
			prevProps.sessionIdx === nextProps.sessionIdx
		);
	},
);

import type { SessionScore } from "../../../utils/score-utils";

interface HeroEndgameProps {
	event: Event;
	eventState: string;
	selectedSessionIdx: number | null;
	calculateTotalScores: () => SessionScore[];
	onAddSession: () => Promise<void>;
	canAddSession: boolean;
	onShare: () => void;
	onReceiveResults: () => void;
	hasJoined: boolean;
}

const PLACEHOLDER_IMG = `${import.meta.env.VITE_APP_WEBSITE_URL}/img/event-page/game_placeholder_thumbnail.png`;

const HeroEndgame = ({
	event,
	eventState,
	selectedSessionIdx,
	calculateTotalScores,
	onAddSession: _onAddSession,
	canAddSession: _canAddSession,
	onShare,
	onReceiveResults,
	hasJoined,
}: HeroEndgameProps) => {
	// Determine background image: cover_img > placeholder
	const fallbackImg = event.cover_img || PLACEHOLDER_IMG;

	// Session UI mode (session selected via hash)
	const scores = calculateTotalScores();
	const sessionScore =
		selectedSessionIdx !== null
			? scores[selectedSessionIdx]
			: { team0: 0, team1: 0 };

	const selectedSession =
		selectedSessionIdx !== null ? event.sessions?.[selectedSessionIdx] : null;
	const selectedSessionPlaybackUrl = selectedSession?.playback_url;

	// Derive team names from session team_mapping with fallback
	const teamMapping = selectedSession?.team_mapping;
	const teamAName = teamMapping?.["0"] || "Team A";
	const teamBName = teamMapping?.["1"] || "Team B";

	// Get display name and formatted time for session
	const displayName =
		selectedSessionIdx === null
			? event.name
			: selectedSession?.name || `Session ${selectedSessionIdx + 1}`;

	const displayTime = formatHeroDateTime(
		selectedSessionIdx === null
			? event.started_at
			: (selectedSession?.started_at ?? event.started_at),
	);

	// Enable highlight editing when session has highlights or is archived (processing complete)
	const hasHighlights =
		selectedSessionIdx === null
			? (event.sessions?.some(
					(session) =>
						(session.highlights && session.highlights.length > 0) ||
						(session as any).is_archived,
				) ?? false)
			: (selectedSession?.highlights?.length ?? 0) > 0 ||
				!!(selectedSession as any)?.is_archived;

	// Event UI mode (no session selected)
	if (selectedSessionIdx === null) {
		return (
			<div className="relative flex flex-col items-center justify-between w-full h-[500px] pb-4 lg:pb-10 px-[calc(50vw-600px)] text-white">
				<img
					className="absolute top-[0] left-[0] w-full h-full object-cover"
					src={fallbackImg}
					alt={event.name || "Event placeholder"}
				/>
				<div
					className="shadow-top absolute top-[0] left-[0] w-full h-36"
					style={{
						background:
							"linear-gradient(180deg, rgba(0, 0, 0, 0.7) 13.81%, rgba(0, 0, 0, 0) 100%)",
					}}
				></div>
				<div
					className="shadow-bottom absolute bottom-[0] left-[0] w-full h-[281px]"
					style={{
						background:
							"linear-gradient(180deg, rgba(0, 0, 0, 0) 13.81%, rgba(0, 0, 0, 0.7) 100%)",
					}}
				></div>
				<div className="relative w-full mt-auto px-5 lg:px-0 pb-1 lg:pb-0 flex items-end justify-between">
					<div className="game-info text-white">
						<Chips
							type="Grey"
							size="Small"
							label={
								eventState === EVENT_STATES.ENDGAME_PROCESSING
									? "Started"
									: "Complete"
							}
						/>
						<p className="mt-4 text-sm font-medium">{displayTime}</p>
						<p className="mt-2 text-3xl lg:text-4xl font-medium">
							{displayName}
						</p>
						{(event.location || event.booker) && (
							<div className="mt-4 flex gap-5">
								{event.location && (
									<div className="flex items-center gap-1">
										<Icon iconName="map" size="Large" color="#999" />
										<p className="text-sm text-color-Grey500">
											{event.location}
										</p>
									</div>
								)}
								{event.booker && (
									<div className="flex items-center gap-1">
										<Icon iconName="user_s_fill" size="Large" color="#999" />
										<p className="text-sm text-color-Grey500">{event.booker}</p>
									</div>
								)}
							</div>
						)}
					</div>
					<div className="flex gap-3 items-center">
						<div className="hidden lg:inline-flex gap-3">
							{hasJoined ? (
								<>
									<BlockButton
										type="Primary"
										width="183px"
										icon=""
										size="Large"
										onClick={() => {
											window.open("https://injoy.neuinx.com/", "_blank");
										}}
										disabled={!hasHighlights}
										loading={false}
										loadingIcon=""
									>
										Edit Highlight in App
									</BlockButton>
									<BlockButton
										type="Secondary"
										width="140px"
										icon="check_outline"
										size="Large"
										disabled={true}
										loading={false}
										loadingIcon=""
									>
										Subscribed
									</BlockButton>
								</>
							) : (
								<BlockButton
									type="Primary"
									width="200px"
									icon=""
									size="Large"
									onClick={onReceiveResults}
									disabled={false}
									loading={false}
									loadingIcon=""
								>
									Receive Game Results
								</BlockButton>
							)}
						</div>
						<IconButton
							type="TertiaryWhite"
							icon="share_outline"
							size={isPC() ? "Large" : "Small"}
							disabled={false}
							loading={false}
							loadingIcon=""
							onClick={onShare}
						/>
					</div>
				</div>
				{hasJoined && (
					<div className="relative w-full pt-8 px-5 flex lg:hidden gap-x-3">
						<BlockButton
							type="Secondary"
							width="100%"
							icon="check_outline"
							size="Large"
							disabled={true}
							loading={false}
							loadingIcon=""
						>
							Subscribed
						</BlockButton>
						{selectedSessionIdx !== null && (
							<BlockButton
								type="Primary"
								width="100%"
								icon=""
								size="Large"
								onClick={() => {
									if (
										selectedSessionIdx !== null &&
										event.sessions?.[selectedSessionIdx]?.sharelink
									) {
										window.open(
											event.sessions[selectedSessionIdx].sharelink,
											"_blank",
										);
									}
								}}
								disabled={!hasHighlights}
								loading={false}
								loadingIcon=""
							>
								Edit Highlight
							</BlockButton>
						)}
					</div>
				)}
			</div>
		);
	}

	return (
		<div className="relative flex flex-col justify-between w-full h-[500px] lg:h-[550px] pt-5 lg:pt-4 pb-4 lg:pb-[38px] px-5 lg:pl-[161px] lg:pr-[214px]">
			{selectedSession &&
			selectedSessionPlaybackUrl &&
			selectedSessionIdx !== null ? (
				<div className="absolute top-[0] left-[0] w-full h-full flex items-center justify-center overflow-hidden">
					<MemoizedHlsPlayer
						src={selectedSessionPlaybackUrl}
						poster={fallbackImg}
						sessionIdx={selectedSessionIdx}
					/>
				</div>
			) : (
				<img
					className="absolute top-[0] left-[0] w-full h-full object-cover"
					src={fallbackImg}
					alt={event.name || "Game placeholder"}
				/>
			)}
			<div
				className="shadow-top absolute top-[0] left-[0] w-full h-36"
				style={{
					background:
						"linear-gradient(0deg, rgba(0, 0, 0, 0) 13.81%, rgba(0, 0, 0, 0.7) 100%)",
				}}
			></div>
			<div
				className="shadow-bottom absolute bottom-[0] left-[0] w-full h-[281px]"
				style={{
					background:
						"linear-gradient(180deg, rgba(0, 0, 0, 0) 13.81%, rgba(0, 0, 0, 0.7) 100%)",
				}}
			></div>
			<div className="scores relative flex justify-between items-center gap-2 w-full lg:w-fit mx-auto text-white">
				<div className="flex flex-col lg:flex-row gap-2 items-center">
					<div className="flex items-center justify-center w-6 h-6 rounded-xl overflow-hidden bg-white">
						<img
							src="/img/event-page/team_light.svg"
							alt={teamAName}
							className="w-6 h-6"
						/>
					</div>
					<p className="text-sm font-medium">{teamAName}</p>
				</div>
				<p className="text-4xl font-medium">{sessionScore.team0}</p>
				<div className="block w-2 h-2 border-t-transparent border-b-transparent border-r-white border-t-4 border-b-4 border-r-8"></div>
				<p className="text-sm font-medium mx-[38.5px]">Final</p>
				<p className="text-4xl font-medium">{sessionScore.team1}</p>
				<div className="flex flex-col-reverse lg:flex-row gap-2 items-center">
					<p className="text-sm font-medium">{teamBName}</p>
					<div className="flex items-center justify-center w-6 h-6 rounded-xl overflow-hidden bg-white">
						<img
							src="/img/event-page/team_dark.svg"
							alt={teamBName}
							className="w-6 h-6"
						/>
					</div>
				</div>
			</div>
			<div className="relative w-full pb-1 lg:pb-0 flex-1 flex items-end justify-between">
				<div className="game-info text-white">
					<Chips
						type="Grey"
						size="Small"
						label={
							eventState === EVENT_STATES.ENDGAME_PROCESSING
								? "Processing"
								: "Complete"
						}
					/>
					<p className="mt-4 text-sm font-medium drop-shadow-[0_1px_12px_0px_rgba(0,0,0,0.12)]">
						{displayTime}
					</p>
					<p className="mt-2 text-3xl lg:text-4xl">{displayName}</p>
				</div>
				<div className="flex gap-3 items-center">
					<div className="hidden lg:inline-flex gap-3">
						{hasJoined ? (
							<>
								<BlockButton
									type="Primary"
									width="183px"
									icon=""
									size="Large"
									onClick={() => {
										if (
											selectedSessionIdx !== null &&
											event.sessions?.[selectedSessionIdx]?.sharelink
										) {
											window.open(
												event.sessions[selectedSessionIdx].sharelink,
												"_blank",
											);
										}
									}}
									disabled={!hasHighlights}
									loading={false}
									loadingIcon=""
								>
									Edit Highlight in App
								</BlockButton>
								<BlockButton
									type="Secondary"
									width="140px"
									icon="check_outline"
									size="Large"
									disabled={true}
									loading={false}
									loadingIcon=""
								>
									Subscribed
								</BlockButton>
							</>
						) : (
							<BlockButton
								type="Primary"
								width="200px"
								icon=""
								size="Large"
								onClick={onReceiveResults}
								disabled={false}
								loading={false}
								loadingIcon=""
							>
								Receive Game Results
							</BlockButton>
						)}
					</div>
					<IconButton
						type="TertiaryWhite"
						icon="share_outline"
						size={isPC() ? "Large" : "Small"}
						disabled={false}
						loading={false}
						loadingIcon=""
						onClick={onShare}
					/>
				</div>
			</div>
			{hasJoined && (
				<div className="relative w-full pt-8 flex lg:hidden gap-x-3">
					<BlockButton
						type="Secondary"
						width="100%"
						icon="check_outline"
						size="Large"
						disabled={true}
						loading={false}
						loadingIcon=""
					>
						Subscribed
					</BlockButton>
					{selectedSessionIdx !== null && (
						<BlockButton
							type="Primary"
							width="100%"
							icon=""
							size="Large"
							onClick={() => {
								if (
									selectedSessionIdx !== null &&
									event.sessions?.[selectedSessionIdx]?.sharelink
								) {
									window.open(
										event.sessions[selectedSessionIdx].sharelink,
										"_blank",
									);
								}
							}}
							disabled={!hasHighlights}
							loading={false}
							loadingIcon=""
						>
							Edit Highlight
						</BlockButton>
					)}
				</div>
			)}
		</div>
	);
};

export { HeroEndgame };
